import styled from 'styled-components';
import { GREY_300, GREY_500 } from '../../../../../../constants';
import { BusinessHealthResponse, HealthScoreComponentStatus } from '../../../types';

const ValueContainer = styled.div`
    font-size: 0.875rem;
    font-weight: normal;
    color: ${GREY_300};
    line-height: 1.75rem;
    letter-spacing: -0.01em;

    & > b {
        color: ${GREY_500};
    }
`;

const buildChannelsValue = ({
    status,
    channels = [],
}: {
    status?: HealthScoreComponentStatus,
    channels?: BusinessHealthResponse['channels'],
}) => {
    const total = channels.length;
    const countUnderperformingChannels = channels.filter(
        (channel) => {
            const channelWinRate = channel.metrics.win_rate?.current || 0;
            const channelBaseline = channel.baselines.win_rate || 0;
            return channelWinRate < channelBaseline;
        },
    ).length;

    if (countUnderperformingChannels === 0) {
        switch (status) {
            case 'stable':
                return <ValueContainer>Performance is stable</ValueContainer>;
            case 'at_risk':
                return <ValueContainer>Performance is at risk</ValueContainer>;
            case 'critical':
                return <ValueContainer>Performance is critical</ValueContainer>;
            default:
                return <ValueContainer>All channels are performing well</ValueContainer>;
        }
    }

    if (countUnderperformingChannels === total) {
        switch (status) {
            case 'at_risk':
                return <ValueContainer>All channels are performing poorly</ValueContainer>;
            case 'critical':
                return <ValueContainer>All channels are performing very poorly</ValueContainer>;
            default:
                return <ValueContainer>All channels need attention</ValueContainer>;
        }
    }

    const noun = countUnderperformingChannels === 1 ? 'channel' : 'channels';

    if (status === 'healthy' || status === 'stable') {
        const verb = countUnderperformingChannels === 1 ? 'needs' : 'need';
        return (
            <ValueContainer>
                Performance is
                {' '}
                {status}
                .
                {' '}
                <b>{countUnderperformingChannels}</b>
                {' '}
                {noun}
                {' '}
                {verb}
                {' '}
                attention
            </ValueContainer>
        );
    }

    const verb = countUnderperformingChannels === 1 ? 'is' : 'are';
    const severity = status === 'critical' ? 'very poorly' : 'poorly';

    return (
        <ValueContainer>
            {`Performance is ${status}. `}
            <b>{countUnderperformingChannels}</b>
            {' '}
            {noun}
            {' '}
            {verb}
            {' '}
            performing
            {' '}
            {severity}
        </ValueContainer>
    );
};

export default buildChannelsValue;
